import { z } from "zod";
export const EmailSchema = z.string().email().toLowerCase().trim();
export const PasswordSchema = z
    .string()
    .min(8, "Passwort muss mindestens 8 Zeichen haben")
    .max(128);
export const LoginSchema = z.object({
    email: EmailSchema,
    password: z.string().min(1),
});
export const RegisterSchema = z.object({
    tenant_name: z.string().min(2).max(80),
    tenant_slug: z
        .string()
        .min(2)
        .max(40)
        .regex(/^[a-z0-9-]+$/, "Slug darf nur Kleinbuchstaben, Zahlen und Bindestriche enthalten"),
    email: EmailSchema,
    password: PasswordSchema,
    display_name: z.string().min(2).max(80).optional(),
});
export const BrandingSchema = z.object({
    name: z.string().max(120).optional(),
    claim: z.string().max(240).optional(),
    colors: z
        .object({
        primary: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        secondary: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        accent: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        background: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        text: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    })
        .partial()
        .optional(),
    logo_url: z.string().url().optional(),
    favicon_url: z.string().url().optional(),
    social: z.record(z.string().url()).optional(),
    donate_url: z.string().url().optional(),
    newsletter_url: z.string().url().optional(),
    footer_md: z.string().max(5000).optional(),
});
export const TenantSlugParam = z.object({
    slug: z.string().regex(/^[a-z0-9-]+$/),
});
export const IdParam = z.object({
    id: z.string().uuid(),
});
export const ChannelSlugParam = z.object({
    slug: z.string().regex(/^[a-z0-9-]+$/),
});
export const ChannelCreateSchema = z.object({
    name: z.string().min(2).max(80),
    slug: z
        .string()
        .min(2)
        .max(40)
        .regex(/^[a-z0-9-]+$/, "Slug darf nur Kleinbuchstaben, Zahlen und Bindestriche enthalten"),
    status: z.enum(["draft", "active", "paused", "archived"]).default("draft"),
    delivery_modes: z
        .object({
        webradio: z.boolean().default(true),
        dab_plus: z.boolean().optional(),
        fm: z.boolean().optional(),
        am: z.boolean().optional(),
    })
        .default({ webradio: true }),
});
export const ChannelUpdateSchema = z.object({
    name: z.string().min(2).max(80).optional(),
    status: z.enum(["draft", "active", "paused", "archived"]).optional(),
    delivery_modes: z
        .object({
        webradio: z.boolean().optional(),
        dab_plus: z.boolean().optional(),
        fm: z.boolean().optional(),
        am: z.boolean().optional(),
    })
        .optional(),
});
const OutputKindEnum = z.enum(["icecast", "shoutcast", "hls"]);
const CodecEnum = z.enum(["mp3", "aac", "aac_plus", "opus", "ogg"]);
// Mounts landen in Server-Konfigurationen und URLs: nur unkritische Zeichen.
const MountSchema = z
    .string()
    .max(120)
    .regex(/^\/[A-Za-z0-9._\-/]+$/, "Mount beginnt mit / und enthält nur Buchstaben, Ziffern, . _ - /");
const HlsVariantsSchema = z.array(z.number().int().min(32).max(320)).min(1).max(4);
export const OutputEndpointCreateSchema = z
    .object({
    kind: OutputKindEnum,
    server_id: z.string().uuid().optional(),
    codec: CodecEnum,
    bitrate_kbps: z.number().int().min(32).max(320),
    mount: MountSchema.optional(),
    /** SHOUTcast-Stream-Nummer; ohne Angabe vergibt die Suite die nächste freie. */
    sid: z.number().int().min(1).max(1000).optional(),
    hls_variants: HlsVariantsSchema.optional(),
    enabled: z.boolean().default(true),
})
    .superRefine((val, ctx) => {
    if (val.kind !== "hls" && !val.server_id) {
        ctx.addIssue({ code: "custom", path: ["server_id"], message: "Server ist für Icecast und SHOUTcast erforderlich" });
    }
    if (val.kind !== "hls" && !val.mount) {
        ctx.addIssue({ code: "custom", path: ["mount"], message: "Mount ist für Icecast und SHOUTcast erforderlich" });
    }
    if (val.kind === "shoutcast" && !["mp3", "aac", "aac_plus"].includes(val.codec)) {
        ctx.addIssue({ code: "custom", path: ["codec"], message: "SHOUTcast überträgt nur MP3 und AAC" });
    }
    if (val.kind === "hls" && val.codec !== "aac") {
        ctx.addIssue({ code: "custom", path: ["codec"], message: "HLS wird als AAC ausgeliefert" });
    }
});
export const OutputEndpointUpdateSchema = z.object({
    codec: CodecEnum.optional(),
    bitrate_kbps: z.number().int().min(32).max(320).optional(),
    mount: MountSchema.optional(),
    hls_variants: HlsVariantsSchema.optional(),
    enabled: z.boolean().optional(),
});
export const PlaylistCreateSchema = z.object({
    name: z.string().min(2).max(120),
    slug: z.string().min(2).max(60).regex(/^[a-z0-9-]+$/),
    channel_id: z.string().uuid().optional().nullable(),
    kind: z.enum(["plain", "smart"]).default("plain"),
    rotation_mode: z.enum(["sequential", "shuffle", "random", "weighted"]).default("sequential"),
    smart_rules: z.record(z.unknown()).default({}),
    default_crossfade_ms: z.number().int().min(0).max(20000).default(2000),
});
export const PlaylistUpdateSchema = z.object({
    name: z.string().min(2).max(120).optional(),
    rotation_mode: z.enum(["sequential", "shuffle", "random", "weighted"]).optional(),
    smart_rules: z.record(z.unknown()).optional(),
    default_crossfade_ms: z.number().int().min(0).max(20000).optional(),
});
export const PlaylistItemSchema = z.object({
    item_id: z.string().uuid(),
    position: z.number().int().min(0),
    fade_in_ms: z.number().int().min(0).max(30000).default(0),
    fade_out_ms: z.number().int().min(0).max(30000).default(0),
    skip_ratio: z.number().min(0).max(1).optional().nullable(),
});
export const PlaylistItemsBulkSchema = z.object({
    items: z.array(PlaylistItemSchema).max(2000),
});
export const JingleCreateSchema = z.object({
    name: z.string().min(1).max(80),
    slot_index: z.number().int().min(0).max(99),
    library_item_id: z.string().uuid(),
    hotkey: z.string().max(20).optional().nullable(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#ff5500"),
    gain_db: z.number().min(-24).max(12).default(0),
});
export const JingleUpdateSchema = z.object({
    name: z.string().min(1).max(80).optional(),
    hotkey: z.string().max(20).optional().nullable(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    gain_db: z.number().min(-24).max(12).optional(),
});
export const LibraryCategorySchema = z.enum(["music", "jingle", "ad", "news", "speech", "voicetrack"]);
export const LibraryUpdateSchema = z.object({
    title: z.string().min(1).max(240).optional(),
    artist: z.string().max(240).optional().nullable(),
    album: z.string().max(240).optional().nullable(),
    category: LibraryCategorySchema.optional(),
    composer: z.string().max(240).optional().nullable(),
    publisher: z.string().max(240).optional().nullable(),
    label: z.string().max(240).optional().nullable(),
    isrc: z.string().regex(/^[A-Z]{2}[A-Z0-9]{3}\d{7}$/, "ISRC wie DEA012600001 (12 Zeichen, ohne Bindestriche)").optional().nullable().or(z.literal("")),
    year: z.number().int().min(1900).max(2100).optional().nullable(),
    track_no: z.number().int().min(1).max(9999).optional().nullable(),
    tags: z.array(z.string().min(1).max(60)).max(50).optional(),
    credits: z
        .array(z.object({
        person: z.string().min(1).max(120),
        role: z.string().min(1).max(60),
    }))
        .max(20)
        .optional(),
    rights_entries: z
        .array(z.object({
        territory: z.string().min(2).max(8).default("WW"),
        type: z.string().min(1).max(40).default("unknown"),
        starts_at: z.string().datetime().optional().nullable(),
        ends_at: z.string().datetime().optional().nullable(),
        notes: z.string().max(500).optional().nullable(),
    }))
        .max(20)
        .optional(),
    source_url: z.string().url().optional().nullable(),
    cover_url: z.string().url().optional().nullable(),
});
export const LibraryQuerySchema = z.object({
    q: z.string().max(120).optional(),
    tag: z.string().max(60).optional(),
    artist: z.string().max(120).optional(),
    category: z.enum(["music", "jingle", "ad", "news", "speech", "voicetrack"]).optional(),
    /** Tempobereich in Schlägen je Minute. */
    bpm_min: z.coerce.number().min(30).max(300).optional(),
    bpm_max: z.coerce.number().min(30).max(300).optional(),
    /** Camelot-Kürzel, z. B. "8A" – für harmonisch passende Nachbarn. */
    camelot: z.string().regex(/^(1[0-2]|[1-9])[AB]$/, 'Camelot wie "8A" oder "12B"').optional(),
    /** Nur Titel, deren Tempo mindestens so sicher gemessen wurde. */
    min_bpm_confidence: z.coerce.number().min(0).max(1).optional(),
    /** Nur Titel ganz ohne Tempowert – für die Lückensuche. */
    ohne_bpm: z.coerce.boolean().optional(),
    sort: z.enum(["neu", "titel", "interpret", "bpm", "bpm_ab", "tonart"]).default("neu"),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(10000).default(0),
});
/**
 * Korrektur von Hand. Wer hier etwas einträgt, hat hingehört – der Wert wird als
 * 'manual' vermerkt und von keiner Messung mehr angefasst. `null` löscht die
 * Korrektur wieder und gibt den Wert für die nächste Messung frei.
 */
export const TaktUpdateSchema = z
    .object({
    bpm: z.number().min(30).max(300).nullable().optional(),
    beats_per_bar: z.number().int().min(2).max(12).nullable().optional(),
    first_beat_ms: z.number().min(0).max(3_600_000).nullable().optional(),
    musical_key: z.string().max(24).nullable().optional(),
    musical_key_camelot: z
        .string()
        .regex(/^(1[0-2]|[1-9])[AB]$/, 'Camelot wie "8A" oder "12B"')
        .nullable()
        .optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Es wurde nichts angegeben" });
/**
 * Eine Marke setzen. Der Platz steht im Pfad, hier steht nur, was darauf soll.
 *
 * `position_ms` ist Pflicht: Eine Marke ohne Stelle gibt es nicht. Farbe und
 * Beschriftung sind freiwillig – wer nichts angibt, bekommt die Standardfarbe des
 * Platzes und keinen Text.
 */
export const HotCueSetSchema = z.object({
    position_ms: z.number().int().min(0).max(24 * 60 * 60 * 1000),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Farbe wie "#e5484d"')
        .nullable()
        .optional(),
    label: z.string().max(40).nullable().optional(),
});
/** Der Platz einer Marke: 0 bis 7, dieselben acht Knöpfe wie am Deck. */
export const HotCueIndexParam = z.object({
    id: z.string().uuid(),
    index: z.coerce.number().int().min(0).max(7),
});
/**
 * Ein Bild: entweder eine vollständige Adresse oder ein Pfad im eigenen Radio
 * (so gibt der Bildspeicher es zurück: „/media/…"). Beides muss gehen, sonst
 * kann man aus der eigenen Bildverwaltung kein Foto wählen.
 */
const bildQuelle = z.string().trim().max(400).refine((v) => v === "" || /^https?:\/\/[^\s"'<>]+$/i.test(v) || /^\/(?!\/)[A-Za-z0-9/._-]{1,200}$/.test(v), "Bitte eine vollständige Adresse (https://…) oder ein Bild aus dem eigenen Radio");
export const ModeratorCreateSchema = z.object({
    display_name: z.string().min(2).max(120),
    slug: z.string().min(2).max(60).regex(/^[a-z0-9-]+$/),
    user_id: z.string().uuid().optional().nullable(),
    /** Die kleine Zeile über dem Namen: „Moderatorin", „Nachrichten", „Technik". */
    rolle: z.string().max(60).optional().nullable(),
    /** Zwischenüberschrift auf der Teamseite: „Moderation", „Nachrichtenredaktion". */
    gruppe: z.string().max(60).optional().nullable(),
    /** Kleiner = weiter vorn. Gleiche Zahl heisst alphabetisch. */
    position: z.number().int().min(-999).max(999).optional(),
    bio_md: z.string().max(5000).optional().nullable(),
    photo_url: bildQuelle.optional().nullable(),
    social: z.record(z.string().url()).optional(),
    contact_email: z.string().email().optional().nullable(),
    is_public: z.boolean().default(false),
});
export const ModeratorUpdateSchema = z.object({
    display_name: z.string().min(2).max(120).optional(),
    rolle: z.string().max(60).optional().nullable(),
    gruppe: z.string().max(60).optional().nullable(),
    position: z.number().int().min(-999).max(999).optional(),
    bio_md: z.string().max(5000).optional().nullable(),
    photo_url: bildQuelle.optional().nullable(),
    social: z.record(z.string().url()).optional(),
    contact_email: z.string().email().optional().nullable(),
    is_public: z.boolean().optional(),
});
export const ShowCreateSchema = z.object({
    title: z.string().min(2).max(120),
    slug: z.string().min(2).max(60).regex(/^[a-z0-9-]+$/),
    channel_id: z.string().uuid().optional().nullable(),
    description_md: z.string().max(10000).optional().nullable(),
    cover_url: z.string().url().optional().nullable(),
    default_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#ff5500"),
    moderator_id: z.string().uuid().optional().nullable(),
    podcast_enabled: z.boolean().default(false),
    intro_audio_id: z.string().uuid().optional().nullable(),
});
export const ShowUpdateSchema = ShowCreateSchema.partial();
export const ScheduleCreateSchema = z.object({
    name: z.string().min(2).max(120),
    is_default: z.boolean().default(false),
    valid_from: z.string().optional().nullable(),
    valid_until: z.string().optional().nullable(),
});
export const ScheduleBlockBaseSchema = z.object({
    weekday: z.number().int().min(0).max(6),
    start_time: z.string().regex(/^\d{2}:\d{2}(?::\d{2})?$/),
    end_time: z.string().regex(/^\d{2}:\d{2}(?::\d{2})?$/),
    kind: z.enum(["show", "playlist", "clock", "traffic", "news", "jingle", "talk", "live", "off"]).default("show"),
    show_id: z.string().uuid().optional().nullable(),
    clock_id: z.string().uuid().optional().nullable(),
    playlist_id: z.string().uuid().optional().nullable(),
    traffic_slot_id: z.string().uuid().optional().nullable(),
    rotate_pool: z.array(z.object({
        playlist_id: z.string().uuid().optional(),
        show_id: z.string().uuid().optional(),
        weight: z.number().int().min(1).max(100).default(1),
    })).default([]),
    meta: z.record(z.unknown()).default({}),
});
export const ScheduleBlockCreateSchema = ScheduleBlockBaseSchema.superRefine((val, ctx) => {
    if (val.kind === "show" && !val.show_id) {
        ctx.addIssue({ code: "custom", path: ["show_id"], message: "show_id ist für kind=show erforderlich" });
    }
    if (val.kind === "playlist" && !val.playlist_id) {
        ctx.addIssue({ code: "custom", path: ["playlist_id"], message: "playlist_id ist für kind=playlist erforderlich" });
    }
    if (val.kind === "clock" && !val.clock_id) {
        ctx.addIssue({ code: "custom", path: ["clock_id"], message: "clock_id ist für kind=clock erforderlich" });
    }
    if (val.kind === "traffic" && !val.traffic_slot_id) {
        ctx.addIssue({ code: "custom", path: ["traffic_slot_id"], message: "traffic_slot_id ist für kind=traffic erforderlich" });
    }
    if (val.end_time <= val.start_time) {
        ctx.addIssue({ code: "custom", path: ["end_time"], message: "end_time muss nach start_time liegen" });
    }
});
export const ScheduleBlockUpdateSchema = ScheduleBlockBaseSchema.partial();
export const TrafficSlotCreateSchema = z.object({
    advertiser: z.string().min(2).max(120),
    spot_library_id: z.string().uuid().optional().nullable(),
    length_ms: z.number().int().min(500).max(600000),
    weight: z.number().int().min(1).max(1000).default(1),
    daypart_start: z.string().regex(/^\d{2}:\d{2}(?::\d{2})?$/).optional().nullable(),
    daypart_end: z.string().regex(/^\d{2}:\d{2}(?::\d{2})?$/).optional().nullable(),
    starts_at: z.string().optional().nullable(),
    ends_at: z.string().optional().nullable(),
    notes: z.string().max(500).optional().nullable(),
});
export const TrafficSlotUpdateSchema = TrafficSlotCreateSchema.partial();
export const ObsSceneMappingSchema = z.object({
    mode: z.enum(["autodj", "live", "jingle", "off"]),
    obs_connection_id: z.string().uuid().optional().nullable(),
    scene_name: z.string().min(1).max(120),
    enabled: z.boolean().default(true),
});
export const RecordingStartSchema = z.object({
    channel_id: z.string().uuid(),
    title: z.string().max(200).optional(),
    source: z.enum(["manual", "obs", "scheduled"]).default("manual"),
    source_obs_connection_id: z.string().uuid().optional().nullable(),
    notes: z.string().max(500).optional(),
});
export const RecordingStopSchema = z.object({
    duration_ms: z.number().int().min(0).optional(),
    notes: z.string().max(500).optional(),
});
export const OnDemandEpisodeCreateSchema = z.object({
    slug: z.string().min(2).max(80).regex(/^[a-z0-9-]+$/),
    title: z.string().min(2).max(200),
    channel_id: z.string().uuid().optional().nullable(),
    show_id: z.string().uuid().optional().nullable(),
    recording_session_id: z.string().uuid().optional().nullable(),
    audio_library_id: z.string().uuid().optional().nullable(),
    description_md: z.string().max(10000).optional().nullable(),
    cover_url: z.string().url().optional().nullable(),
    duration_ms: z.number().int().min(0).optional().nullable(),
    season: z.number().int().min(1).optional().nullable(),
    episode_number: z.number().int().min(1).optional().nullable(),
    explicit: z.boolean().default(false),
});
export const OnDemandEpisodeUpdateSchema = OnDemandEpisodeCreateSchema.partial().extend({
    published_at: z.string().datetime().optional().nullable(),
});
export const PodcastFeedConfigSchema = z.object({
    title: z.string().min(2).max(200),
    description: z.string().min(10).max(2000),
    author: z.string().max(120).optional().nullable(),
    email: z.string().email().optional().nullable(),
    language: z.string().min(2).max(8).default("de-de"),
    image_url: z.string().url().optional().nullable(),
    category: z.string().min(2).max(60).default("Music"),
    copyright: z.string().max(200).optional().nullable(),
});
// --- Cover und Angaben zur Veröffentlichung (ans Ende gehängt) ---------------
/**
 * Angaben zur Veröffentlichung von Hand ändern.
 *
 * Gesendet heißt „setzen“, weggelassen heißt „lassen“ – wie bei den Feldern für die
 * Meldelisten. `null` leert ein Feld wieder und gibt es für die nächste Suche frei.
 */
export const VeroeffentlichungUpdateSchema = z
    .object({
    album_artist: z.string().max(240).nullable().optional(),
    label: z.string().max(240).nullable().optional(),
    catalog_no: z.string().max(120).nullable().optional(),
    barcode: z.string().max(60).nullable().optional(),
    medium: z.enum(["cd", "vinyl", "digital", "kassette", "radio"]).nullable().optional(),
    disc_no: z.number().int().min(1).max(999).nullable().optional(),
    disc_total: z.number().int().min(1).max(999).nullable().optional(),
    track_total: z.number().int().min(1).max(999).nullable().optional(),
    release_year: z.number().int().min(1900).max(2100).nullable().optional(),
    musicbrainz_release_id: z
        .string()
        .regex(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/, "Das ist keine MusicBrainz-Kennung")
        .nullable()
        .optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Es wurde nichts angegeben" });
/**
 * Der Schalter fürs Nachschlagen im Netz.
 *
 * Ohne Kontakt kein Einschalten: MusicBrainz verlangt eine ansprechbare Kennung, und
 * eine erfundene wäre schlechter als gar keine Abfrage. Deshalb steht die Bedingung
 * hier und nicht nur in der Oberfläche – sonst könnte man sie umgehen.
 */
export const CoverEinstellungenSchema = z
    .object({
    online_enabled: z.boolean(),
    contact: z.string().max(200).nullable().optional(),
})
    .refine((v) => !v.online_enabled || (v.contact ?? "").trim().length > 3, {
    message: "Zum Nachschlagen wird ein Kontakt gebraucht (E-Mail oder Adresse der Radioseite)",
    path: ["contact"],
});
